// Mensajes con la distribuidora (decisión D53): una conversación por negocio, con el seguimiento de sus pedidos.
// Lo que llega por el socket (`tienda.mensaje`, `tienda.lectura`, `tienda.escribiendo`, `tienda.pedido`) se aplica acá;
// la pantalla y la insignia leen de este estado. Lo que se escribe aparece al instante y se manda con un id propio:
// reintentar no lo duplica.
import { create } from 'zustand';
import { uuidv7 } from '../compartido';
import { api, ErrorApi, mensajeDeError, SinRed } from './api';
import type { ConversacionTienda, MensajeTienda, PedidoAdjunto, ResumenChat } from './chat-tipos';
import { insertarMensaje, juntarMensajes } from './chat-logica';
import { olvidarConsultas } from './consultas';
import { achicarFoto, type FotoReducida } from './foto';
import { revisarPush } from './push';
import { useSesion } from './sesion';
import { emitir, escuchar } from './tiempo-real';

interface Pendiente {
  estado: 'enviando' | 'error';
  error?: string;
  texto: string | null;
  foto: FotoReducida | null;
  /** Para mostrar la foto mientras se sube. */
  urlLocal: string | null;
  pedido: PedidoAdjunto | null;
}

interface EstadoChat {
  negocioId: string | null;
  /** null: todavía no se sabe (no se preguntó). */
  habilitado: boolean | null;
  noLeidos: number;
  clavePush: string | null;
  conv: Omit<ConversacionTienda, 'mensajes' | 'hay_mas' | 'no_leidos'> | null;
  mensajes: MensajeTienda[];
  hayMas: boolean;
  cargando: boolean;
  cargandoAnteriores: boolean;
  error: string | null;
  sinRed: boolean;
  pendientes: Record<string, Pendiente>;
  /** "<distribuidora> está escribiendo…" (se apaga solo). */
  escribiendo: string | null;
  /** La pantalla de Mensajes está abierta y a la vista: lo que llega queda leído. */
  mirando: boolean;
}

const inicial = (negocioId: string | null): EstadoChat => ({
  negocioId,
  habilitado: null,
  noLeidos: 0,
  clavePush: null,
  conv: null,
  mensajes: [],
  hayMas: false,
  cargando: false,
  cargandoAnteriores: false,
  error: null,
  sinRed: false,
  pendientes: {},
  escribiendo: null,
  mirando: false,
});

export const useChat = create<EstadoChat>(() => inicial(null));

/** Otro negocio (o salió): se empieza de cero. */
function delNegocio(negocioId: string): void {
  if (useChat.getState().negocioId !== negocioId) {
    for (const p of Object.values(useChat.getState().pendientes)) if (p.urlLocal) URL.revokeObjectURL(p.urlLocal);
    useChat.setState(inicial(negocioId));
  }
}

let resumenEn = 0;

/**
 * Para la insignia: si hay chat y cuántos sin leer (no crea la conversación). Con `siVencido`, no vuelve a preguntar
 * si ya lo hizo hace menos de un minuto (cada pantalla la pide al abrirse; el socket la mantiene al día).
 */
export async function cargarResumen(negocioId: string, opciones: { siVencido?: boolean } = {}): Promise<void> {
  // El preventista (decisión D60) no escribe por el comercio: sin chat ni avisos.
  if (useSesion.getState().sesion?.vendedor) return;
  const cambio = useChat.getState().negocioId !== negocioId;
  delNegocio(negocioId);
  if (opciones.siVencido && !cambio && Date.now() - resumenEn < 60_000) return;
  resumenEn = Date.now();
  try {
    const r = await api<ResumenChat>('/tienda/chat/resumen', { consulta: { cliente_id: negocioId } });
    if (useChat.getState().negocioId !== negocioId) return;
    useChat.setState({ habilitado: r.habilitado, noLeidos: r.no_leidos, clavePush: r.clave_push });
    // Si ya dio permiso para los avisos, la suscripción queda al día con esta sesión (nunca pide permiso sola).
    const sesion = useSesion.getState().sesion?.dispositivo_id;
    if (r.habilitado && sesion) void revisarPush(r.clave_push, sesion);
  } catch {
    resumenEn = 0;
    /* sin señal o sin acceso: la insignia queda como estaba */
  }
}

/** Abre la conversación: los últimos mensajes y las tildes de la distribuidora. */
export async function abrirChat(negocioId: string): Promise<void> {
  delNegocio(negocioId);
  useChat.setState({ cargando: true, error: null, sinRed: false });
  try {
    const r = await api<ConversacionTienda>('/tienda/chat', { consulta: { cliente_id: negocioId } });
    if (useChat.getState().negocioId !== negocioId) return;
    const { mensajes, hay_mas, no_leidos, ...conv } = r;
    useChat.setState((s) => ({
      habilitado: true,
      conv,
      // Lo que todavía se está mandando queda al final.
      mensajes: juntarMensajes(
        mensajes,
        s.mensajes.filter((m) => !m.seq),
      ),
      hayMas: hay_mas,
      noLeidos: no_leidos,
      cargando: false,
    }));
  } catch (e) {
    if (useChat.getState().negocioId !== negocioId) return;
    const apagado = e instanceof ErrorApi && e.codigo === 'CHAT_APAGADO';
    useChat.setState({
      cargando: false,
      habilitado: apagado ? false : useChat.getState().habilitado,
      error: mensajeDeError(e),
      sinRed: e instanceof SinRed,
    });
  }
}

export async function cargarAnteriores(): Promise<void> {
  const s = useChat.getState();
  const primero = s.mensajes.find((m) => m.seq > 0);
  if (!s.negocioId || !s.hayMas || s.cargandoAnteriores || !primero) return;
  useChat.setState({ cargandoAnteriores: true });
  try {
    const r = await api<{ datos: MensajeTienda[]; hay_mas: boolean }>('/tienda/chat/mensajes', {
      consulta: { cliente_id: s.negocioId, antes: primero.seq, limite: 50 },
    });
    useChat.setState((x) => ({ mensajes: juntarMensajes(x.mensajes, r.datos), hayMas: r.hay_mas }));
  } finally {
    useChat.setState({ cargandoAnteriores: false });
  }
}

/** Mientras se manda: aparece enseguida con lo que se escribió, a la derecha, sin tildes. */
function local(id: string, p: Pendiente): MensajeTienda {
  return {
    id,
    seq: 0,
    propio: true,
    de: 'cliente',
    autor: null,
    tipo: p.foto ? 'imagen' : 'texto',
    texto: p.texto,
    foto: p.foto ? { id: '', ancho: p.foto.ancho, alto: p.foto.alto } : null,
    pedido: p.pedido
      ? { ...p.pedido, estado: null, seguimiento: null, monto: null, bultos: null, fecha_entrega: null }
      : null,
    seguimiento: null,
    responde_a: null,
    creado_en: new Date().toISOString(),
    editado: false,
    eliminado: false,
  };
}

/** Escribe a la distribuidora: texto, una foto (se achica antes) o las dos, con un pedido si habla de él. */
export async function enviarMensaje(datos: { texto: string; foto?: File | null; pedido?: PedidoAdjunto | null }) {
  const negocioId = useChat.getState().negocioId;
  if (!negocioId) return;
  const texto = datos.texto.trim() || null;
  if (!texto && !datos.foto) return;
  let foto: FotoReducida | null = null;
  if (datos.foto) foto = await achicarFoto(datos.foto);
  const id = uuidv7();
  const p: Pendiente = {
    estado: 'enviando',
    texto,
    foto,
    urlLocal: foto ? URL.createObjectURL(foto.blob) : null,
    pedido: datos.pedido ?? null,
  };
  useChat.setState((s) => ({
    pendientes: { ...s.pendientes, [id]: p },
    mensajes: insertarMensaje(s.mensajes, local(id, p)),
  }));
  await mandar(negocioId, id);
}

async function mandar(negocioId: string, id: string): Promise<void> {
  const p = useChat.getState().pendientes[id];
  if (!p) return;
  useChat.setState((s) => ({ pendientes: { ...s.pendientes, [id]: { ...p, estado: 'enviando', error: undefined } } }));
  try {
    let archivoId: string | null = null;
    if (p.foto) {
      const s = await api<{ archivo_id: string; url: string; cabeceras: Record<string, string> }>(
        '/tienda/chat/fotos',
        { cuerpo: { cliente_id: negocioId, mime: p.foto.mime, bytes: p.foto.blob.size } },
      );
      const subida = await fetch(s.url, { method: 'PUT', body: p.foto.blob, headers: s.cabeceras }).catch(() => null);
      if (!subida?.ok) throw new SinRed();
      await api(`/tienda/chat/fotos/${s.archivo_id}/confirmar`, {
        cuerpo: { ancho: p.foto.ancho, alto: p.foto.alto },
        consulta: { cliente_id: negocioId },
      });
      archivoId = s.archivo_id;
    }
    const m = await api<MensajeTienda>('/tienda/chat/mensajes', {
      cuerpo: {
        cliente_id: negocioId,
        id,
        ...(archivoId ? { tipo: 'imagen', archivo_id: archivoId } : { tipo: 'texto' }),
        ...(p.texto ? { texto: p.texto } : {}),
        ...(p.pedido ? { pedido_venta_id: p.pedido.id } : {}),
      },
      timeoutMs: 30_000,
    });
    if (p.urlLocal) URL.revokeObjectURL(p.urlLocal);
    useChat.setState((s) => {
      const { [id]: _hecho, ...resto } = s.pendientes;
      return { pendientes: resto, mensajes: insertarMensaje(s.mensajes, m) };
    });
  } catch (e) {
    useChat.setState((s) => ({
      pendientes: { ...s.pendientes, [id]: { ...p, estado: 'error', error: mensajeDeError(e) } },
    }));
  }
}

export function reintentar(id: string): void {
  const negocioId = useChat.getState().negocioId;
  if (negocioId) void mandar(negocioId, id);
}

export function descartar(id: string): void {
  useChat.setState((s) => {
    const p = s.pendientes[id];
    if (p?.urlLocal) URL.revokeObjectURL(p.urlLocal);
    const { [id]: _fuera, ...resto } = s.pendientes;
    return { pendientes: resto, mensajes: s.mensajes.filter((m) => m.id !== id) };
  });
}

/** Con la pantalla a la vista: lo que llegó queda leído (y la distribuidora ve las tildes). */
export async function marcarLeido(): Promise<void> {
  const s = useChat.getState();
  const ultimo = s.mensajes.reduce((n, m) => Math.max(n, m.seq), 0);
  if (!s.negocioId || !s.conv || ultimo <= s.conv.leido_seq) return;
  useChat.setState({ conv: { ...s.conv, leido_seq: ultimo }, noLeidos: 0 });
  try {
    const r = await api<{ leido_seq: number; no_leidos: number }>('/tienda/chat/leido', {
      cuerpo: { cliente_id: s.negocioId, hasta_seq: ultimo },
    });
    useChat.setState({ noLeidos: r.no_leidos });
  } catch {
    /* se vuelve a marcar con el próximo mensaje */
  }
}

export function mirandoChat(mirando: boolean): void {
  useChat.setState({ mirando });
}

let ultimoAviso = 0;
/** "Escribiendo…" para la distribuidora: uno cada 3 segundos como mucho. */
export function avisarEscribiendo(): void {
  const negocioId = useChat.getState().negocioId;
  if (!negocioId || Date.now() - ultimoAviso < 3_000) return;
  ultimoAviso = Date.now();
  emitir('chat.escribiendo', { cliente_id: negocioId });
}

// ---------- Lo que llega por el socket ----------

let apagarEscribiendo: ReturnType<typeof setTimeout> | null = null;
const oyentesPedido = new Set<(pedidoId: string) => void>();

/** La pantalla de un pedido se entera cuando cambia (para mostrar lo nuevo sin esperar). */
export function alCambiarPedido(f: (pedidoId: string) => void): () => void {
  oyentesPedido.add(f);
  return () => {
    oyentesPedido.delete(f);
  };
}

escuchar('tienda.mensaje', (d) => {
  const { cliente_id, mensaje } = d as { cliente_id: string; mensaje: MensajeTienda };
  const s = useChat.getState();
  if (cliente_id !== s.negocioId) return;
  useChat.setState({
    mensajes: insertarMensaje(s.mensajes, mensaje),
    escribiendo: mensaje.propio ? s.escribiendo : null,
  });
  if (mensaje.propio) return;
  if (s.mirando && document.visibilityState === 'visible') void marcarLeido();
  // Fuera de la pantalla de Mensajes, la insignia se pone al día (cuenta lo que avisa, no lo que hizo el comercio).
  else void cargarResumen(cliente_id);
});

escuchar('tienda.lectura', (d) => {
  const { cliente_id, distribuidora } = d as { cliente_id: string; distribuidora: ConversacionTienda['distribuidora'] };
  const s = useChat.getState();
  if (cliente_id === s.negocioId && s.conv) useChat.setState({ conv: { ...s.conv, distribuidora } });
});

escuchar('tienda.escribiendo', (d) => {
  const { cliente_id, nombre } = d as { cliente_id: string; nombre: string };
  if (cliente_id !== useChat.getState().negocioId) return;
  useChat.setState({ escribiendo: nombre });
  if (apagarEscribiendo) clearTimeout(apagarEscribiendo);
  apagarEscribiendo = setTimeout(() => useChat.setState({ escribiendo: null }), 5_000);
});

escuchar('tienda.pedido', (d) => {
  const { cliente_id, pedido_venta_id } = d as { cliente_id: string; pedido_venta_id: string };
  // Lo guardado del pedido y de la lista ya no sirve: se vuelve a pedir al mirarlo.
  olvidarConsultas(`pedidos:${cliente_id}`);
  oyentesPedido.forEach((f) => f(pedido_venta_id));
});

// Al reconectar (sin señal un rato): lo que se perdió se trae de nuevo.
escuchar('conectado', () => {
  const s = useChat.getState();
  if (!s.negocioId) return;
  if (s.conv) void abrirChat(s.negocioId);
  else void cargarResumen(s.negocioId);
});

// ---------- Fotos ----------

const fotos = new Map<string, { url: string; vence: number }>();

/** Dirección para ver una foto de la conversación (vale una hora; se guarda un rato). */
export async function urlFoto(archivoId: string, negocioId: string): Promise<string> {
  const ya = fotos.get(archivoId);
  if (ya && ya.vence > Date.now()) return ya.url;
  const r = await api<{ url: string }>(`/tienda/chat/fotos/${archivoId}`, { consulta: { cliente_id: negocioId } });
  fotos.set(archivoId, { url: r.url, vence: Date.now() + 50 * 60_000 });
  return r.url;
}
